import { Menu } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AvoAccordian,
  AvoAccordianPanel,
  AvoAccordianTrigger,
} from 'components/utils/AvoAccordian';
import { AvoDnd, AvoDndItem } from 'components/utils/AvoDnd';
import { AvoEditor } from 'components/utils/avoeditor/AvoEditor';
import { ToolbarButton } from 'components/utils/draftJS/utils';
import { Icons } from 'components/utils/Icons';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { SlidePanel } from 'components/utils/panels/AvoSlidePanel';
import { RichTextMenuButtonTypes, SuggestionTypes } from 'components/utils/tiptap/tiptapInterfaces';
import { Body1, Body2 } from 'components/utils/typo';
import { ChevronDown } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { getNumerics, getSuggestions, getVariables } from 'utils/suggestions';
import { convertCodeToText, generateID } from 'utils/utilityFunctions';
import Button from '../../../utils/Button';
import { ConditionalTextConditionForm } from './ConditionalTextConditionForm';
import {
  additionalCondition,
  Condition,
  ConditionalTextFormVariable,
  defaultCondition,
} from './ConditionalTextForm';

export const ConditionalTextConditions = () => {
  const [isDragDisabled, setIsDragDisabled] = useState(false);
  const { watch, setValue, clearErrors } = useFormContext<ConditionalTextFormVariable>();
  const conditions = watch('conditions').filter((condition) => !condition.is_default);
  const currentDefaultCondition =
    watch('conditions').find((condition) => condition.is_default) || defaultCondition;

  const addCondition = () => {
    setValue(
      'conditions',
      [
        ...conditions,
        {
          ...additionalCondition,
          id: generateID(),
          label: `Condition ${watch('conditions').length}`,
        },
        currentDefaultCondition,
      ],
      { shouldDirty: true }
    );
    clearErrors();
  };

  return (
    <div className='space-y-1 rounded border border-gray-200 bg-gray-100 p-1'>
      <AvoDnd
        items={conditions}
        className='space-y-1'
        onChange={(newConditions) => {
          setValue('conditions', [...newConditions, currentDefaultCondition], {
            shouldDirty: true,
          });
          clearErrors();
        }}
      >
        {({ items }) =>
          items.map((item, idx) => (
            <AvoDndItem
              key={item.id}
              itemId={item.id + ''}
              index={idx}
              isDragDisabled={isDragDisabled}
            >
              <ConditionItem condition={item} idx={idx} setIsDragDisabled={setIsDragDisabled} />
            </AvoDndItem>
          ))
        }
      </AvoDnd>
      <ConditionItem
        condition={currentDefaultCondition}
        idx={watch('conditions').length - 1}
        setIsDragDisabled={setIsDragDisabled}
      />
      <div onClick={addCondition} className='cursor-pointer'>
        <div className='flex gap-0.5 px-3.5 py-[9px]'>
          <Icons.Plus className='w-3 fill-primary-600' />
          <p className='text-button-1 font-semibold text-primary-600'>Add Condition</p>
        </div>
      </div>
    </div>
  );
};

interface ConditionItemProps {
  condition: Condition;
  idx: number;
  setIsDragDisabled: (bool) => void;
}

const suggestionsToExclude: SuggestionTypes[] = [
  'link',
  'reference',
  // 'knowledge_base',
  'conditional_text',
  'media',
  'contact_number',
  'ehr_order',
];
const richTextButtonsShowList: RichTextMenuButtonTypes[] = [
  'textStyles',
  'bold',
  'italic',
  'highlight',
  'bulletList',
  'orderedList',
  'more',
];

const ConditionItem = ({ condition, idx, setIsDragDisabled }: ConditionItemProps) => {
  const [queryBuilderFormOpen, setQueryBuilderFormOpen] = useState(false);
  const knowledgeBase = useSelector((state: any) => state.knowledgeBaseState.knowledgeBases) || [];
  const { module } = useContext(ModuleContext);

  useEffect(() => {
    setIsDragDisabled(queryBuilderFormOpen);
  }, [queryBuilderFormOpen]);

  const { data: numerics } = useQuery({
    queryKey: ['numerics', module?.id],
    queryFn: () => getNumerics(module?.id, module?.type),
    enabled: !!module?.id,
    initialData: [],
    initialDataUpdatedAt: 0,
    staleTime: Infinity,
  });
  const { data: variables } = useQuery({
    queryKey: ['variables', module?.id],
    queryFn: () => getVariables(module?.id, module?.type),
    enabled: !!module?.id,
    initialData: [],
    initialDataUpdatedAt: 0,
    staleTime: Infinity,
  });

  const { data: suggestion } = useQuery({
    queryKey: ['draftjsSuggestions', module?.type, module?.id],
    queryFn: () => getSuggestions(module?.id, true, module?.type),
    enabled: !!module?.id,
    initialData: [],
    initialDataUpdatedAt: 0,
    staleTime: Infinity,
  });

  const { setValue, watch, clearErrors } = useFormContext<ConditionalTextFormVariable>();

  const onDraftJSChange = (e: any) => {
    const text = e.blocks.map((block) => block.text).join('\n');
    setValue(
      'conditions',
      watch('conditions').map((item, index) =>
        index === idx ? { ...item, text, text_content: e } : item
      ),
      { shouldDirty: true }
    );
    clearErrors();
  };

  const onTiptapChange = (editor) => {
    setValue(
      'conditions',
      watch('conditions').map((item, index) =>
        index === idx
          ? { ...item, text: editor.getText(), text_content_tiptap: editor.getJSON() }
          : item
      ),
      { shouldDirty: true }
    );
    clearErrors();
  };

  return (
    <>
      <div className='rounded border border-gray-300 bg-white px-4 py-3.5'>
        <AvoAccordian>
          <AvoAccordianTrigger>
            {({ isOpen }) => (
              <div className='flex items-center gap-2'>
                <ChevronDown
                  size={20}
                  strokeWidth={1.5}
                  className={clsx('shrink-0 transition-all', isOpen && 'rotate-180')}
                />
                {isOpen ? (
                  <>
                    <input
                      className='grow border-b border-gray-300 text-body-1 focus:outline-0 disabled:bg-transparent'
                      value={condition.is_default ? 'None of the above (Default)' : condition.label}
                      onChange={(e) => {
                        setValue(
                          'conditions',
                          watch('conditions').map((item, index) =>
                            index === idx ? { ...item, label: e.target.value } : item
                          ),
                          { shouldDirty: true }
                        );
                        clearErrors();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      disabled={condition.is_default}
                    />
                    {!condition.is_default && (
                      <Menu
                        as='div'
                        className='relative shrink-0'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {({ open }) => (
                          <>
                            <Menu.Button
                              as='p'
                              className={twMerge(
                                'm-0 flex h-[20px] w-[20px] items-center justify-center rounded',
                                open && 'bg-gray-600'
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icons.ThreeDot className={open ? 'fill-white' : 'fill-gray-600'} />
                            </Menu.Button>
                            <Menu.Items
                              className={twMerge(
                                'absolute right-0 z-10 m-0 w-[100px] rounded bg-white shadow-md',
                                'outline-0'
                              )}
                            >
                              <Menu.Item
                                className='mb-0 h-[40px] w-[100px] px-3 py-[10px] hover:bg-primary-bg-strong hover:text-white'
                                as='p'
                                onClick={() => {
                                  setValue(
                                    'conditions',
                                    [
                                      ...watch('conditions').slice(0, idx + 1),
                                      { ...condition, id: generateID() },
                                      ...watch('conditions').slice(idx + 1),
                                    ],
                                    { shouldDirty: true }
                                  );
                                  clearErrors();
                                }}
                              >
                                <Body2>Duplicate</Body2>
                              </Menu.Item>
                              <Menu.Item
                                as='p'
                                className='mb-0 h-[40px] w-[100px] px-3 py-[10px] hover:bg-primary-bg-strong hover:text-white'
                                onClick={() => {
                                  setValue(
                                    'conditions',
                                    watch('conditions').filter((_, index) => index != idx),
                                    { shouldDirty: true }
                                  );
                                  clearErrors();
                                }}
                              >
                                <Body2>Delete</Body2>
                              </Menu.Item>
                            </Menu.Items>
                          </>
                        )}
                      </Menu>
                    )}
                  </>
                ) : (
                  <Body1 className='grow truncate'>
                    {condition.is_default ? 'None of the above (Default)' : condition.label}
                  </Body1>
                )}
              </div>
            )}
          </AvoAccordianTrigger>
          <AvoAccordianPanel>
            {!condition.is_default && (
              <div className='mt-3 flex gap-2'>
                <div className='flex min-w-0 grow items-center rounded border border-gray-300 bg-gray-100 px-3'>
                  <Body2 className='truncate'>{convertCodeToText(condition.condition)}</Body2>
                </div>
                <div className='w-[100px] shrink-0'>
                  <Button.Outline
                    type='button'
                    className='!text-gray-700'
                    onClick={() => setQueryBuilderFormOpen(true)}
                  >
                    Edit
                  </Button.Outline>
                </div>
              </div>
            )}
            <div className='mt-3'>
              <AvoEditor
                conditionalText
                suggestions={suggestion}
                setValue={onDraftJSChange}
                prevValue={condition.text_content}
                disableConditionalText={true}
                numerics={numerics}
                moduleId={module?.id}
                variables={variables}
                knowledgeBases={knowledgeBase}
                moduleType={module?.type}
                richTextButtonShowList={[
                  ToolbarButton.TEXT_STYLE,
                  ToolbarButton.BOLD,
                  ToolbarButton.ITALIC,
                  ToolbarButton.HIGHLIGHT,
                  ToolbarButton.INFOBOX,
                  ToolbarButton.INSERT_LINK,
                  ToolbarButton.VARIABLES,
                ]}
                wrapperClassNames='flex-grow max-h-[600px] min-h-[280px] !h-auto'
                onUpdate={onTiptapChange}
                initialContent={condition.text_content_tiptap}
                suggestionsToExclude={suggestionsToExclude}
                richTextButtonsShowListTiptap={richTextButtonsShowList}
              />
            </div>
          </AvoAccordianPanel>
        </AvoAccordian>
      </div>
      <SlidePanel open={queryBuilderFormOpen}>
        <ConditionalTextConditionForm
          onCancel={() => setQueryBuilderFormOpen(false)}
          onSave={(condition, conditionJson, conditionLogic) => {
            setValue(
              'conditions',
              watch('conditions').map((item, index) =>
                idx === index
                  ? {
                      ...item,
                      condition,
                      condition_logic: conditionLogic,
                      builder_condition_json: conditionJson,
                    }
                  : item
              ),
              { shouldDirty: true }
            );
            setQueryBuilderFormOpen(false);
          }}
          conditionData={condition}
        />
      </SlidePanel>
    </>
  );
};
